import {
  UndefinedInitialDataInfiniteOptions,
  useInfiniteQuery,
} from "@tanstack/react-query"
import { Nullable } from "../../@client/System"
import type * as Api from "../../@client/api"
import { get } from "../get"
import { useHandleAsyncSafely } from "./use-handle-async-safely"

type PageInfo = {
  after: Nullable<number>
  before: Nullable<number>
  take: Nullable<number>
}

export function useInfiniteGet<ActionName extends keyof Api.GET>(
  url: ActionName,
  input: (pageInfo: PageInfo) => Api.Input[ActionName],
  getNextPageParam: (
    lastPage: Api.Output[ActionName],
    pages: any[]
  ) => PageInfo,
  queryOptions?: Omit<
    UndefinedInitialDataInfiniteOptions<
      Api.Output[ActionName],
      Error,
      { pages: Api.Output[ActionName][] },
      (ActionName | Api.Input[ActionName])[],
      PageInfo
    >,
    "queryKey"
  >,
  requestInit?: RequestInit
) {
  const handleAsyncSafely = useHandleAsyncSafely<Api.Output[ActionName]>()

  return useInfiniteQuery<
    Api.Output[ActionName],
    Error,
    {
      pages: Api.Output[ActionName][]
    },
    (ActionName | Api.Input[ActionName])[],
    PageInfo
  >({
    retry: false,
    queryKey: [url],
    initialPageParam: { after: null, before: null, take: null },
    ...queryOptions,
    getNextPageParam,
    queryFn: (params) =>
      handleAsyncSafely(() =>
        get(
          url,
          input(params.pageParam),
          requestInit ? requestInit : { headers: {} }
        )
      ),
  })
}
